import _toConsumableArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state"],
    _excluded2 = ["state"],
    _excluded3 = ["state"];

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { memo, useCallback, useContext, useState, useRef, createContext } from 'react';
import { toArray } from './array.js';
import { useInitialValue, useWrapElement, useLazyValue, useSafeLayoutEffect } from './hooks.js';
import { shallowEqual } from './misc.js';
import { createComponent } from './system.js';
import { jsx } from 'react/jsx-runtime';
var GET_STATE = Symbol("getState");
var SUBSCRIBE = Symbol("subscribe");
var TIMESTAMP = Symbol("timestamp");
var INITIAL_CONTEXT = Symbol("initialContext");

function getState(state) {
  if (!state) return state;
  var fn = state[GET_STATE];
  if (fn) return fn();
  return state;
}

function hasSubscribe(state) {
  if (!state) return false;
  return !!state[SUBSCRIBE];
}

function getSubscribe(state) {
  if (!state) return;
  return state[SUBSCRIBE];
}

function getLatest(a, b) {
  if (!b) return a;
  if (!a) return b;
  if (!(TIMESTAMP in b)) return a;
  if (!(TIMESTAMP in a)) return b;
  if (a[TIMESTAMP] >= b[TIMESTAMP]) return a;
  return b;
}

function defineGetState(state, currentState) {
  if (currentState === void 0) {
    currentState = state;
  }

  Object.defineProperty(state, GET_STATE, {
    value: function value() {
      return currentState;
    },
    writable: true
  });
}

function defineSubscribe(state, subscribe) {
  if (!(SUBSCRIBE in state)) {
    Object.defineProperty(state, SUBSCRIBE, {
      value: subscribe
    });
  }
}

function defineTimestamp(state) {
  if (!(TIMESTAMP in state)) {
    Object.defineProperty(state, TIMESTAMP, {
      value: Date.now(),
      writable: true
    });
  }
}

function patchState(state) {
  Object.defineProperty(state, TIMESTAMP, {
    value: Date.now(),
    writable: true
  });
}

function defineInitialContext(context) {
  var initialContext = /*#__PURE__*/createContext(undefined);
  Object.defineProperty(context, INITIAL_CONTEXT, {
    value: initialContext
  });
  return initialContext;
}

function hasInitialContext(stateOrContext) {
  return stateOrContext && INITIAL_CONTEXT in stateOrContext;
}

function getInitialContext(context) {
  if (!hasInitialContext(context)) return;
  var ctx = context;
  return ctx[INITIAL_CONTEXT];
}
/**
 * Creates a context that can be passed to `useStore` and `useStoreProvider`.
 */


function createStoreContext() {
  var context = /*#__PURE__*/createContext(undefined);
  defineInitialContext(context);
  return context;
}
/**
 * Creates a type-safe component with the `as` prop, `state` prop,
 * `React.forwardRef` and `React.memo`.
 *
 * @example
 * import { Options, createMemoComponent } from "ariakit-utils/store";
 *
 * type Props = Options<"div"> & {
 *   state?: { customProp: boolean };
 * };
 *
 * const Component = createMemoComponent<Props>(
 *   ({ state, ...props }) => <div {...props} />
 * );
 *
 * <Component as="button" state={{ customProp: true }} />
 */


function createMemoComponent(render, propsAreEqual) {
  if (propsAreEqual === void 0) {
    propsAreEqual = shallowEqual;
  }

  var Role = createComponent(render);
  return /*#__PURE__*/memo(Role, function (prev, next) {
    var prevState = prev.state,
        prevProps = _objectWithoutProperties(prev, _excluded);

    var nextState = next.state,
        nextProps = _objectWithoutProperties(next, _excluded2);

    if (nextState && hasSubscribe(nextState)) {
      return propsAreEqual(prevProps, nextProps);
    }

    return propsAreEqual(prev, next);
  });
}
/**
 * Returns props with a `wrapElement` function that wraps an element with a
 * React Context Provider that provides a store context to consumers.
 * @example
 * import * as React from "react";
 * import { useStoreProvider } from "ariakit-utils/store";
 *
 * const StoreContext = createStoreContext();
 *
 * function Component({ state, ...props }) {
 *   const { wrapElement } = useStoreProvider({ state, ...props }, StoreContext);
 *   return wrapElement(<div {...props} />);
 * }
 */


function useStoreProvider(_ref, context) {
  var state = _ref.state,
      props = _objectWithoutProperties(_ref, _excluded3);

  var initialValue = useInitialValue(state);
  var value = state && hasSubscribe(state) ? initialValue : state;
  defineGetState(value, state);
  var initialContext = getInitialContext(context);
  return useWrapElement(props, function (element) {
    if (value && initialContext) {
      element = /*#__PURE__*/jsx(initialContext.Provider, {
        value: value,
        children: element
      });
    }

    if (state) {
      element = /*#__PURE__*/jsx(context.Provider, {
        value: state,
        children: element
      });
    }

    return element;
  }, [value, initialContext, state, context]);
}
/**
 * Adds publishing capabilities to state so it can be passed to `useStore` or
 * `useStoreProvider` later.
 * @example
 * import { useStorePublisher } from "ariakit-utils/store";
 *
 * function useComponentState() {
 *   const state = React.useMemo(() => ({ a: "a" }), []);
 *   return useStorePublisher(state);
 * }
 */


function useStorePublisher(state) {
  var listeners = useLazyValue(function () {
    return new Set();
  });
  useSafeLayoutEffect(function () {
    patchState(state);

    var _iterator = _createForOfIteratorHelper(listeners),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var listener = _step.value;
        listener(state);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }, [state]);
  var subscribe = useCallback(function (listener) {
    listeners.add(listener);
    return function () {
      return listeners.delete(listener);
    };
  }, []);
  defineSubscribe(state, subscribe);
  defineGetState(state);
  defineTimestamp(state);
  return state;
}
/**
 * Handles state updates on the state or context state passed as the first
 * argument based on the filter argument.
 * @example
 * import { useStore } from "ariakit-utils/store";
 *
 * const ContextState = createContextState();
 *
 * function Component({ state }) {
 *   state = useStore(state || ContextState, ["stateProp"]);
 * }
 */


function useStore(stateOrContext, filter) {
  var contextState = useContext(getContext(stateOrContext, filter));
  var externalState = hasInitialContext(stateOrContext) ? contextState : stateOrContext;

  var _useState = useState(function () {
    return getState(externalState);
  }),
      _useState2 = _slicedToArray(_useState, 2),
      internalState = _useState2[0],
      setState = _useState2[1];

  var state = hasSubscribe(externalState) && hasSubscribe(internalState) ? getLatest(internalState, externalState) : externalState;
  var subscribe = getSubscribe(externalState);
  var prevStateRef = useRef(null);
  var deps = toArray(filter);
  var noFilter = !filter;
  useSafeLayoutEffect(function () {
    if (!subscribe || !setState) return;
    if (noFilter) return subscribe(setState);
    if (!deps.length) return;
    return subscribe(function (nextState) {
      var prevState = prevStateRef.current;
      prevStateRef.current = nextState;

      var filterDep = function filterDep(dep) {
        if (typeof dep === "function") {
          var result = dep(nextState); // TODO: We probably need different functions for:
          // useStore(context, [(nextState) => nextState.activeId === id]);
          // useStore(context, [(nextState) => nextState.booleanProp]);
          // Because in the second case we want to compare the result with the
          // previous state result.

          if (typeof result === "boolean") {
            return result || prevState && dep(prevState);
          } else if (prevState) {
            return result !== dep(prevState);
          }

          return result;
        }

        var key = dep;
        return (prevState == null ? void 0 : prevState[key]) !== nextState[key];
      };

      if (deps.some(filterDep)) {
        setState(nextState);
      }
    });
  }, [subscribe, setState, noFilter].concat(_toConsumableArray(deps)));
  return state;
}

var EmptyContext = /*#__PURE__*/createContext(undefined);

function getContext(stateOrContext, filter) {
  if (!hasInitialContext(stateOrContext)) {
    return EmptyContext;
  }

  if (filter) {
    return getInitialContext(stateOrContext);
  }

  return stateOrContext;
}

export { createMemoComponent, createStoreContext, useStore, useStoreProvider, useStorePublisher };